const englishCommercialTaxRuleMessages = {
  title: 'Commercial Tax Rules',
  description:
    'Manage versioned, effective-dated tax configuration for priced itineraries.',
  create: 'Add tax rule',
  createTitle: 'Create commercial tax rule',
  editTitle: 'Create next tax-rule version',
  versionNotice:
    'Saving an edit creates a new version. Historical priced-itinerary tax snapshots never change.',
  country: 'Registration country',
  rate: 'Rate (%)',
  effectiveFrom: 'Effective from',
  effectiveTo: 'Effective to (optional)',
  inclusive: 'Dates are inclusive.',
  status: 'Status',
  version: 'Version',
  usage: 'Priced-delivery usage',
  used: '{count} priced delivery | {count} priced deliveries',
  unused: 'Not yet used',
  reason: 'Audit reason',
  save: 'Save version',
  active: 'Active',
  inactive: 'Inactive',
  activate: 'Activate',
  deactivate: 'Deactivate',
  actions: 'Tax-rule actions',
  loading: 'Loading commercial tax rules…',
  empty: 'No commercial tax rules are configured.',
  loadError: 'Commercial tax rules could not be loaded.',
  permissionRestricted:
    'You do not have organization-scoped access to these tax rules.',
  readOnly: 'You can view tax-rule history but cannot change it.',
  retry: 'Retry',
  cancel: 'Cancel',
  continue: 'Continue',
  statusReasonTitle: 'Reason for status change',
  confirmActivateTitle: 'Activate commercial tax rule?',
  confirmActivate:
    'Activate {country} version {version}? Overlapping active coverage is not allowed.',
  confirmDeactivateTitle: 'Deactivate commercial tax rule?',
  confirmDeactivate:
    'Deactivate {country} version {version}? This may leave issue dates without an active tax rule. Priced-itinerary Send will be blocked for affected issue dates.',
  created: 'Commercial tax rule created.',
  updated: 'A new commercial tax-rule version was created.',
  activated: 'Commercial tax rule activated.',
  deactivated: 'Commercial tax rule deactivated.',
  validation:
    'Enter a valid ISO country code, rate from 0 to 100, date range and audit reason.',
  overlap: 'This period overlaps another active tax rule.',
  versionConflict:
    'The tax rule changed. Refresh and review the latest version.',
  loadingPermissions: 'Checking permissions…',
  readOnlyTitle: 'Read-only tax configuration',
  saveError: 'The tax rule could not be saved.',
  saving: 'Saving…',
  reasonRequired: 'Enter an audit reason before continuing.',
  noSelection: 'No commercial tax rule is selected.',
  openVersion: 'Open {country} tax rule version {version}',
  reloadLatest: 'Reload latest version',
  yesActivate: 'Yes, activate',
  yesDeactivate: 'Yes, deactivate',
  no: 'No',
  activationReasonContext:
    'Record why {country} version {version} should be activated.',
  deactivationReasonContext:
    'Record why {country} version {version} should be deactivated.',
  deactivationCoverageWarning:
    'Deactivation may leave priced-itinerary issue dates without an applicable tax rule.',
  coverageGapTitle: 'Tax-rule coverage gap',
  coverageGapAction:
    'Create or activate an effective-dated rule before sending a priced itinerary for an uncovered issue date.',
  coverageNone: '{country} has no active commercial tax rule.',
  coverageBefore:
    '{country} coverage begins on {date}; earlier issue dates are uncovered.',
  coverageBetween:
    '{country} has no active rule from {from} through {to}, inclusive.',
  coverageAfter: '{country} has no active rule after {date}.',
} as const;

export const commercialTaxRuleMessages = {
  en: englishCommercialTaxRuleMessages,
  ja: {
    ...englishCommercialTaxRuleMessages,
    title: '商用税ルール',
    description:
      'バージョン管理された適用日付きの価格付き旅程税設定を管理します。',
    create: '税ルールを追加',
    createTitle: '商用税ルールを作成',
    editTitle: '次の税ルールバージョンを作成',
    versionNotice:
      '編集を保存すると新しいバージョンが作成されます。過去の価格付き旅程税スナップショットは変更されません。',
    country: '登録国',
    rate: '税率 (%)',
    effectiveFrom: '適用開始日',
    effectiveTo: '適用終了日（任意）',
    inclusive: '開始日と終了日を含みます。',
    status: '状態',
    version: 'バージョン',
    usage: '価格付き配信での使用',
    used: '価格付き配信 {count} 件',
    unused: '未使用',
    reason: '監査理由',
    save: 'バージョンを保存',
    active: '有効',
    inactive: '無効',
    activate: '有効化',
    deactivate: '無効化',
    actions: '税ルール操作',
    loading: '商用税ルールを読み込み中…',
    empty: '商用税ルールが設定されていません。',
    loadError: '商用税ルールを読み込めませんでした。',
    permissionRestricted:
      'この組織の税ルールへのアクセス権がありません。',
    readOnly:
      '履歴を表示できますが変更はできません。',
    retry: '再試行',
    cancel: 'キャンセル',
    continue: '続行',
    statusReasonTitle: '状態変更の理由',
    confirmActivateTitle: '商用税ルールを有効化しますか？',
    confirmDeactivateTitle: '商用税ルールを無効化しますか？',
    created: '商用税ルールを作成しました。',
    updated:
      '新しい商用税ルールバージョンを作成しました。',
    activated: '商用税ルールを有効化しました。',
    deactivated: '商用税ルールを無効化しました。',
    validation:
      '有効な ISO 国コード、0～100 の税率、日付範囲、監査理由を入力してください。',
    overlap:
      'この期間は別の有効な税ルールと重複しています。',
    versionConflict:
      '税ルールが変更されました。最新バージョンを再読み込みして確認してください。',
  },
  'zh-TW': {
    ...englishCommercialTaxRuleMessages,
    title: '商業稅務規則',
    description:
      '管理具版本與生效日期的定價行程稅務設定。',
    create: '新增稅務規則',
    createTitle: '建立商業稅務規則',
    editTitle: '建立下一個稅務規則版本',
    versionNotice:
      '儲存編輯會建立新版本，歷史定價行程稅務快照不會變更。',
    country: '註冊國家',
    rate: '稅率 (%)',
    effectiveFrom: '生效起日',
    effectiveTo: '生效迄日（選填）',
    inclusive: '日期包含起迄日。',
    status: '狀態',
    version: '版本',
    usage: '定價配送使用情況',
    used: '{count} 筆定價配送',
    unused: '尚未使用',
    reason: '稽核原因',
    save: '儲存版本',
    active: '啟用',
    inactive: '停用',
    activate: '啟用',
    deactivate: '停用',
    actions: '稅務規則操作',
    loading: '正在載入商業稅務規則…',
    empty: '尚未設定商業稅務規則。',
    loadError: '無法載入商業稅務規則。',
    permissionRestricted:
      '您沒有此組織範圍的稅務規則存取權。',
    readOnly: '您可以查看歷史，但不能變更。',
    retry: '重試',
    cancel: '取消',
    continue: '繼續',
    statusReasonTitle: '狀態變更原因',
    confirmActivateTitle: '啟用商業稅務規則？',
    confirmDeactivateTitle: '停用商業稅務規則？',
    created: '已建立商業稅務規則。',
    updated: '已建立新的商業稅務規則版本。',
    activated: '已啟用商業稅務規則。',
    deactivated: '已停用商業稅務規則。',
    validation:
      '請輸入有效的 ISO 國家代碼、0 至 100 稅率、日期範圍與稽核原因。',
    overlap: '此期間與另一啟用中的稅務規則重疊。',
    versionConflict:
      '稅務規則已變更，請重新整理並檢查最新版本。',
  },
  ko: {
    ...englishCommercialTaxRuleMessages,
    title: '상업 세금 규칙',
    description:
      '버전과 적용일을 관리하는 가격 포함 여행 일정 세금 설정을 관리합니다.',
    create: '세금 규칙 추가',
    createTitle: '상업 세금 규칙 생성',
    editTitle: '다음 세금 규칙 버전 생성',
    versionNotice:
      '편집을 저장하면 새 버전이 생성되며 과거 가격 포함 여행 일정 세금 스냅샷은 변경되지 않습니다.',
    country: '등록 국가',
    rate: '세율 (%)',
    effectiveFrom: '적용 시작일',
    effectiveTo: '적용 종료일(선택)',
    inclusive: '시작일과 종료일을 포함합니다.',
    status: '상태',
    version: '버전',
    usage: '가격 포함 배송 사용',
    used: '가격 포함 배송 {count}건',
    unused: '사용되지 않음',
    reason: '감사 사유',
    save: '버전 저장',
    active: '활성',
    inactive: '비활성',
    activate: '활성화',
    deactivate: '비활성화',
    actions: '세금 규칙 작업',
    loading: '상업 세금 규칙을 불러오는 중…',
    empty: '구성된 상업 세금 규칙이 없습니다.',
    loadError: '상업 세금 규칙을 불러올 수 없습니다.',
    permissionRestricted:
      '이 조직 범위의 세금 규칙에 접근할 권한이 없습니다.',
    readOnly:
      '이력은 볼 수 있지만 변경할 수 없습니다.',
    retry: '다시 시도',
    cancel: '취소',
    continue: '계속',
    statusReasonTitle: '상태 변경 사유',
    confirmActivateTitle: '상업 세금 규칙을 활성화하시겠습니까?',
    confirmDeactivateTitle: '상업 세금 규칙을 비활성화하시겠습니까?',
    created: '상업 세금 규칙을 생성했습니다.',
    updated:
      '새 상업 세금 규칙 버전을 생성했습니다.',
    activated: '상업 세금 규칙을 활성화했습니다.',
    deactivated: '상업 세금 규칙을 비활성화했습니다.',
    validation:
      '올바른 ISO 국가 코드, 0~100 세율, 날짜 범위 및 감사 사유를 입력하세요.',
    overlap: '이 기간은 다른 활성 세금 규칙과 겹칩니다.',
    versionConflict:
      '세금 규칙이 변경되었습니다. 최신 버전을 새로 고쳐 검토하세요.',
  },
} as const;
